import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  Apps as AppIcon,
} from '@mui/icons-material';

// InstallPrompt — fixed top-right card that offers the user a
// one-click PWA install. Modelled on the standard `beforeinstallprompt`
// flow: Chrome / Edge fire that event when the manifest + service
// worker are valid; we capture it, suppress the browser's own banner,
// and surface our own button instead. On click we call prompt() and
// the OS dialog handles the rest — once installed, Chrome adds a
// shortcut named after manifest.short_name ("My School System") and
// launches the standalone window automatically.
//
// "Cancel" just dismisses our card and stores the dismissal in
// localStorage so we don't re-pester the user the next time they open
// a page. They can still install later from the browser's address-bar
// install icon.
const DISMISSED_KEY = 'mss_install_dismissed_at';
const DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator?.standalone === true;
}

function wasRecentlyDismissed() {
  try {
    const t = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    return t > 0 && Date.now() - t < DISMISS_FOR_MS;
  } catch { return false; }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen]         = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;

    const onBefore = (e) => {
      // Stash the event so we can fire it from our own button.
      e.preventDefault();
      setDeferred(e);
      if (!wasRecentlyDismissed()) setOpen(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setOpen(false);
      setDeferred(null);
      // Clear the dismissal so a future uninstall + reinstall flow
      // still gets to see the banner.
      try { localStorage.removeItem(DISMISSED_KEY); } catch { /* ignore */ }
    };

    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch {
      // User cancelled in the OS dialog — nothing to do.
    } finally {
      setDeferred(null);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };

  if (installed || !open || !deferred) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: (t) => t.zIndex.snackbar + 1,
        width: 320,
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.2}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
          color: '#fff', flex: '0 0 auto',
        }}>
          <AppIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
            Install My School System
          </Typography>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mt: 0.4, lineHeight: 1.35 }}>
            Add a desktop shortcut for faster access. It will open in its own window.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained" size="small" startIcon={<InstallIcon />}
              onClick={handleInstall}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Install
            </Button>
            <Button
              variant="text" size="small" color="inherit"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Cancel
            </Button>
          </Stack>
        </Box>
        <IconButton size="small" onClick={handleCancel}
          sx={{ flex: '0 0 auto', mt: -0.5, mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}
